import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  ChevronDown,
  Plus,
  PlusCircle,
} from "lucide-react";
import { Button } from "@akuna/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { AppointmentFormResponsiveDialog } from "@/components/calendar/AppointmentFormResponsiveDialog";
import { PetExamUploadDialog } from "@/components/pets/PetExamUploadDialog";
import { PrescriptionComposer } from "@/components/prescriptions/PrescriptionComposer";
import { PrescriptionDialogLayout } from "@/components/prescriptions/editor/PrescriptionDialogLayout";
import { PrescriptionViewDialog } from "@/components/prescriptions/PrescriptionViewDialog";
import { VaccinationFormDialog } from "@/components/applications/ApplicationFormDialog";
import { PatientTermsPanel } from "@/components/terms/PatientTermsPanel";
import { getPatientEventTypeIcon } from "@/lib/event-icons";
import { formatLocalTimeHHmm } from "@/lib/date-utils";
import {
  type PrescriptionWithItems,
  useDeleteMedicalPrescription,
} from "@/hooks/useMedicalPrescriptions";
import { invalidatePatientsCache } from "@/lib/react-query/invalidatePatientsCache";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface PatientActionsMenuProps {
  petId: string;
  ownerId: string | null;
  clinicId: string | null;
  speciesId?: string | null;
  canStartConsult: boolean;
  onStartConsult: () => void;
  compactTrigger?: boolean;
  triggerClassName?: string;
}

export function PatientActionsMenu({
  petId,
  ownerId,
  clinicId,
  speciesId,
  canStartConsult,
  onStartConsult,
  compactTrigger = false,
  triggerClassName,
}: PatientActionsMenuProps) {
  const { t } = useTranslation("patients");
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isAppointmentOpen, setIsAppointmentOpen] = useState(false);
  const [isExamOpen, setIsExamOpen] = useState(false);
  const [isPrescriptionOpen, setIsPrescriptionOpen] = useState(false);
  const [previewPrescription, setPreviewPrescription] = useState<PrescriptionWithItems | null>(null);
  const [isPrescriptionPreviewOpen, setIsPrescriptionPreviewOpen] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState<PrescriptionWithItems | null>(null);
  const [prescriptionToDelete, setPrescriptionToDelete] = useState<PrescriptionWithItems | null>(null);
  const [prescriptionFooterEl, setPrescriptionFooterEl] = useState<HTMLDivElement | null>(null);
  const [isVaccinationOpen, setIsVaccinationOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isTermsExpanded, setIsTermsExpanded] = useState(false);
  const [termsFooterEl, setTermsFooterEl] = useState<HTMLDivElement | null>(null);
  const termsModelInputRef = useRef<HTMLInputElement>(null);
  const deletePrescription = useDeleteMedicalPrescription();

  const { hasPermission: canCreateMedicalRecords, isLoading: isLoadingMedicalRecordPermission } =
    useHasPermissionSync("medical_records", "create");
  const { hasPermission: canCreateAppointments, isLoading: isLoadingAppointmentsPermission } =
    useHasPermissionSync("appointments", "create");
  const { hasPermission: canEditPatients, isLoading: isLoadingPatientsPermission } =
    useHasPermissionSync("patients", "edit");
  const { hasPermission: canCreateApplications, isLoading: isLoadingApplicationsPermission } =
    useHasPermissionSync("applications", "create");
  const { hasPermission: canViewTerms, isLoading: isLoadingTermsPermission } =
    useHasPermissionSync("terms", "view");

  const canOpenConsultAction = canStartConsult && canCreateMedicalRecords && !isLoadingMedicalRecordPermission;
  const canOpenAppointmentAction =
    Boolean(clinicId) &&
    Boolean(ownerId) &&
    Boolean(petId) &&
    canCreateAppointments &&
    !isLoadingAppointmentsPermission;
  const canOpenExamAction = Boolean(clinicId) && Boolean(petId) && canEditPatients && !isLoadingPatientsPermission;
  const canOpenPrescriptionAction =
    Boolean(clinicId) && Boolean(petId) && canEditPatients && !isLoadingPatientsPermission;
  const canOpenVaccinationAction =
    Boolean(clinicId) &&
    Boolean(petId) &&
    canCreateApplications &&
    !isLoadingApplicationsPermission;
  const canOpenTermsAction = Boolean(clinicId) && Boolean(petId) && canViewTerms && !isLoadingTermsPermission;

  const isAnyPermissionLoading = useMemo(
    () =>
      isLoadingMedicalRecordPermission ||
      isLoadingAppointmentsPermission ||
      isLoadingPatientsPermission ||
      isLoadingApplicationsPermission ||
      isLoadingTermsPermission,
    [
      isLoadingMedicalRecordPermission,
      isLoadingAppointmentsPermission,
      isLoadingPatientsPermission,
      isLoadingApplicationsPermission,
      isLoadingTermsPermission,
    ],
  );

  const closeActions = useCallback(() => {
    setIsMenuOpen(false);
    setIsSheetOpen(false);
  }, []);

  const openDialogFromMenu = useCallback((openDialog: () => void) => {
    closeActions();
    window.setTimeout(openDialog, 0);
  }, [closeActions]);

  const newAppointmentPrefill = useMemo(() => {
    if (!isAppointmentOpen) return null;
    const at = new Date();
    return { date: at, time: formatLocalTimeHHmm(at) };
  }, [isAppointmentOpen]);

  const handleAppointmentSuccess = useCallback(() => {
    setIsAppointmentOpen(false);

    invalidatePatientsCache(queryClient, {
      ownerId,
      petIds: [petId],
    });

    queryClient.invalidateQueries({ queryKey: ["appointments"], refetchType: "all" });
    queryClient.invalidateQueries({ queryKey: ["appointments", "patient-history"], refetchType: "all" });
    queryClient.invalidateQueries({ queryKey: ["patient-history"], refetchType: "all" });
    queryClient.invalidateQueries({ queryKey: ["patient-history-feed"], refetchType: "all" });
    queryClient.invalidateQueries({ queryKey: ["owner-history-timeline"], refetchType: "all" });
    queryClient.invalidateQueries({ queryKey: ["owner-history-feed"], refetchType: "all" });
    queryClient.invalidateQueries({ queryKey: ["future-rows-appointments"], refetchType: "all" });
    queryClient.invalidateQueries({ queryKey: ["application-follow-up-tracking"], refetchType: "all" });
  }, [ownerId, petId, queryClient]);

  const sheetActionClassName =
    "flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left text-[15px] font-semibold text-foreground disabled:cursor-not-allowed disabled:opacity-50";

  const actionItems = (
    <>
      <DropdownMenuItem
        disabled={!canOpenConsultAction || isAnyPermissionLoading}
        onSelect={() => {
          closeActions();
          onStartConsult();
        }}
      >
        {getPatientEventTypeIcon("consultation", { tone: "current", className: "mr-2", sizeClass: "h-4 w-4" })}
        {t("patientActions.startConsultation")}
      </DropdownMenuItem>

      <DropdownMenuItem
        disabled={!canOpenAppointmentAction || isAnyPermissionLoading}
        onSelect={(event) => {
          event.preventDefault();
          openDialogFromMenu(() => setIsAppointmentOpen(true));
        }}
      >
        <CalendarDays className="mr-2 h-4 w-4" />
        {t("patientActions.createAppointment")}
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuItem
        disabled={!canOpenExamAction || isAnyPermissionLoading}
        onSelect={(event) => {
          event.preventDefault();
          openDialogFromMenu(() => setIsExamOpen(true));
        }}
      >
        {getPatientEventTypeIcon("exam", { tone: "current", className: "mr-2", sizeClass: "h-4 w-4" })}
        {t("patientActions.uploadDocument")}
      </DropdownMenuItem>

      <DropdownMenuItem
        disabled={!canOpenPrescriptionAction || isAnyPermissionLoading}
        onSelect={(event) => {
          event.preventDefault();
          openDialogFromMenu(() => setIsPrescriptionOpen(true));
        }}
      >
        {getPatientEventTypeIcon("prescription", { tone: "current", className: "mr-2", sizeClass: "h-4 w-4" })}
        {t("patientActions.createPrescription")}
      </DropdownMenuItem>

      <DropdownMenuItem
        disabled={!canOpenVaccinationAction || isAnyPermissionLoading}
        onSelect={(event) => {
          event.preventDefault();
          openDialogFromMenu(() => setIsVaccinationOpen(true));
        }}
      >
        {getPatientEventTypeIcon("vaccine", { tone: "current", className: "mr-2", sizeClass: "h-4 w-4" })}
        {t("patientActions.registerApplication")}
      </DropdownMenuItem>

      <DropdownMenuItem
        disabled={!canOpenTermsAction || isAnyPermissionLoading}
        onSelect={(event) => {
          event.preventDefault();
          openDialogFromMenu(() => setIsTermsOpen(true));
        }}
      >
        {getPatientEventTypeIcon("term", { tone: "current", className: "mr-2", sizeClass: "h-4 w-4" })}
        {t("patientActions.terms")}
      </DropdownMenuItem>
    </>
  );

  const sheetActions = (
    <div className="space-y-2 py-4">
      <button
        type="button"
        disabled={!canOpenConsultAction || isAnyPermissionLoading}
        onClick={() => {
          closeActions();
          onStartConsult();
        }}
        className={sheetActionClassName}
      >
        {getPatientEventTypeIcon("consultation", { tone: "current", className: "text-primary", sizeClass: "h-4 w-4" })}
        {t("patientActions.startConsultation")}
      </button>
      <button
        type="button"
        disabled={!canOpenAppointmentAction || isAnyPermissionLoading}
        onClick={() => openDialogFromMenu(() => setIsAppointmentOpen(true))}
        className={sheetActionClassName}
      >
        <CalendarDays className="h-4 w-4 text-primary" />
        {t("patientActions.createAppointment")}
      </button>
      <div className="h-px bg-border/70" />
      <button
        type="button"
        disabled={!canOpenExamAction || isAnyPermissionLoading}
        onClick={() => openDialogFromMenu(() => setIsExamOpen(true))}
        className={sheetActionClassName}
      >
        {getPatientEventTypeIcon("exam", { tone: "current", className: "text-primary", sizeClass: "h-4 w-4" })}
        {t("patientActions.uploadDocument")}
      </button>
      <button
        type="button"
        disabled={!canOpenPrescriptionAction || isAnyPermissionLoading}
        onClick={() => openDialogFromMenu(() => setIsPrescriptionOpen(true))}
        className={sheetActionClassName}
      >
        {getPatientEventTypeIcon("prescription", { tone: "current", className: "text-primary", sizeClass: "h-4 w-4" })}
        {t("patientActions.createPrescription")}
      </button>
      <button
        type="button"
        disabled={!canOpenVaccinationAction || isAnyPermissionLoading}
        onClick={() => openDialogFromMenu(() => setIsVaccinationOpen(true))}
        className={sheetActionClassName}
      >
        {getPatientEventTypeIcon("vaccine", { tone: "current", className: "text-primary", sizeClass: "h-4 w-4" })}
        {t("patientActions.registerApplication")}
      </button>
      <button
        type="button"
        disabled={!canOpenTermsAction || isAnyPermissionLoading}
        onClick={() => openDialogFromMenu(() => setIsTermsOpen(true))}
        className={sheetActionClassName}
      >
        {getPatientEventTypeIcon("term", { tone: "current", className: "text-primary", sizeClass: "h-4 w-4" })}
        {t("patientActions.terms")}
      </button>
    </div>
  );

  return (
    <>
      {compactTrigger ? (
        <>
          <Button
            size="icon"
            className={cn("h-12 w-12 rounded-full p-0", triggerClassName)}
            aria-label={t("patientActions.quickActions")}
            onClick={() => setIsSheetOpen(true)}
          >
            <Plus className="h-5 w-5" />
          </Button>
          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetContent side="bottom" className="rounded-t-3xl">
              <SheetHeader className="text-left">
                <SheetTitle>{t("patientActions.quickActions")}</SheetTitle>
              </SheetHeader>
              {sheetActions}
            </SheetContent>
          </Sheet>
        </>
      ) : (
      <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            className={cn("w-full min-w-[190px] md:w-auto", triggerClassName)}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            {t("patientActions.quickActions")}
            <ChevronDown className="ml-2 h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[240px]">
          {actionItems}
        </DropdownMenuContent>
      </DropdownMenu>
      )}

      <AppointmentFormResponsiveDialog
        open={isAppointmentOpen && Boolean(clinicId && newAppointmentPrefill)}
        onOpenChange={setIsAppointmentOpen}
        clinicId={clinicId ?? ""}
        prefilledDate={newAppointmentPrefill?.date}
        prefilledTime={newAppointmentPrefill?.time}
        prefilledOwnerId={ownerId ?? undefined}
        prefilledPetId={petId}
        onSuccess={handleAppointmentSuccess}
      />

      <PetExamUploadDialog
        open={isExamOpen}
        onOpenChange={setIsExamOpen}
        renderTrigger={false}
        petId={petId}
        clinicId={clinicId}
      />

      <ResponsiveDialog
        open={isPrescriptionOpen}
        onOpenChange={setIsPrescriptionOpen}
        noScrollViewport
        className="flex max-h-[90vh] max-w-6xl flex-col p-0"
      >
          <div className="shrink-0 border-b border-border/60 bg-white px-6 py-4">
            <DialogHeader className="text-left">
              <DialogTitle>
                {editingPrescription ? t("prescriptions:composer.editPrescription") : t("patientActions.newPrescription")}
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {clinicId ? (
            <PrescriptionDialogLayout petId={petId}>
            <PrescriptionComposer
              petId={petId}
              clinicId={clinicId}
              medicalRecordId={undefined}
              speciesId={speciesId ?? undefined}
              autoOpenPreviewOnCreate={false}
              initialPrescription={editingPrescription}
              onCreatedPrescription={(prescription) => {
                setPreviewPrescription(prescription);
                setEditingPrescription(null);
                setIsPrescriptionOpen(false);
                setIsPrescriptionPreviewOpen(true);
              }}
              onUpdated={() => {
                setEditingPrescription(null);
                setIsPrescriptionOpen(false);
              }}
              isInsideDialog={true}
              footerContainer={prescriptionFooterEl}
              variant={isMobile ? "mobile" : "desktop"}
            />
            </PrescriptionDialogLayout>
          ) : null}
          </div>
          <div className="shrink-0 border-t border-border/60 bg-white px-6 py-4">
            <div ref={setPrescriptionFooterEl} className="flex justify-end gap-2" />
          </div>
      </ResponsiveDialog>

      {previewPrescription ? (
        <PrescriptionViewDialog
          key={previewPrescription.id}
          open={isPrescriptionPreviewOpen}
          onOpenChange={(open) => {
            setIsPrescriptionPreviewOpen(open);
            if (!open) {
              setPreviewPrescription(null);
            }
          }}
          prescription={previewPrescription}
          onEdit={
            !previewPrescription.signed_at
              ? () => {
                  setIsPrescriptionPreviewOpen(false);
                  setEditingPrescription(previewPrescription);
                  setIsPrescriptionOpen(true);
                }
              : undefined
          }
          onDelete={() => {
            setIsPrescriptionPreviewOpen(false);
            setPrescriptionToDelete(previewPrescription);
          }}
          isDeleting={deletePrescription.isPending}
        />
      ) : null}

      <AlertDialog
        open={Boolean(prescriptionToDelete)}
        onOpenChange={(open) => {
          if (!open && !deletePrescription.isPending) {
            setPrescriptionToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("prescriptions:composer.deleteIssuedTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("prescriptions:composer.deleteIssuedDescription", {
                date: prescriptionToDelete?.issued_at
                  ? new Date(prescriptionToDelete.issued_at).toLocaleDateString("pt-BR")
                  : t("prescriptions:print.noDate"),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePrescription.isPending}>
              {t("prescriptions:composer.deleteIssuedCancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deletePrescription.isPending || !prescriptionToDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!prescriptionToDelete) return;
                try {
                  await deletePrescription.mutateAsync(prescriptionToDelete.id);
                  setPrescriptionToDelete(null);
                  setPreviewPrescription(null);
                  setEditingPrescription(null);
                } catch {
                  // feedback handled by mutation
                }
              }}
            >
              {deletePrescription.isPending
                ? t("prescriptions:composer.deleteIssuedLoading")
                : t("prescriptions:composer.deleteIssuedConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {clinicId ? (
        <VaccinationFormDialog
          open={isVaccinationOpen}
          onOpenChange={setIsVaccinationOpen}
          petId={petId}
          clinicId={clinicId}
          medicalRecordId={undefined}
          petSpeciesId={speciesId ?? undefined}
          showCreateAppointmentToggle
        />
      ) : null}

      <ResponsiveDialog
        open={isTermsOpen}
        onOpenChange={(open) => {
          setIsTermsOpen(open);
          if (!open) setIsTermsExpanded(false);
        }}
        noScrollViewport
        className={cn("flex max-h-[90vh] flex-col p-0", isTermsExpanded ? "max-w-3xl" : "max-w-lg")}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          termsModelInputRef.current?.focus();
        }}
      >
          <div className="shrink-0 border-b border-border/60 bg-white px-6 py-4">
            <DialogHeader className="text-left">
              <DialogTitle>{t("patientActions.patientTerms")}</DialogTitle>
            </DialogHeader>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {clinicId ? (
            <PatientTermsPanel
              petId={petId}
              clinicId={clinicId}
              medicalRecordId={null}
              onCloseRequest={() => setIsTermsOpen(false)}
              footerContainer={termsFooterEl}
              onExpandedChange={setIsTermsExpanded}
              modelInputRef={termsModelInputRef}
            />
          ) : null}
          </div>
          <div className="shrink-0 border-t border-border/60 bg-white px-6 py-4">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {isMobile ? null : (
                <Button type="button" variant="outline" onClick={() => setIsTermsOpen(false)}>
                  {t("common:cancel")}
                </Button>
              )}
              <div ref={setTermsFooterEl} className="flex gap-2" />
            </div>
          </div>
      </ResponsiveDialog>
    </>
  );
}
